'use client';

import { SiWhatsapp } from 'react-icons/si';
import { RiFacebookFill } from 'react-icons/ri';
import { FaInstagram } from 'react-icons/fa';
import { BiLogoLinkedin, BiLogoTiktok } from 'react-icons/bi';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { useLanguage } from '@/app/context/language-context';
import { useTheme } from '@/app/components/theme-provider';

export default function Footer() {
  const { t, language } = useLanguage();
  const { theme } = useTheme();

  const getLogoSrc = () => {
    if (theme === 'dark') {
      return language === 'zh'
        ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_white_ch_no_bg.webp'
        : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_white_eng_no_bg.webp';
    }
    if (theme === 'light') {
      return language === 'zh'
        ? 'https://images.x-space.ae/assets/images/website_image/chinese_logo/glitch_landscape_black_ch_no_bg.webp'
        : 'https://images.x-space.ae/assets/images/website_image/english_logo/xs_logo_black_eng_no_bg.webp';
    }
  };

  return (
    <footer className='bg-gray-50 dark:bg-black/30'>
      <div className='mx-auto max-w-7xl px-4 pt-8 pb-8 sm:px-6 lg:px-8'>
        {/* Mobile Footer */}
        <div className='lg:hidden'>
          <div className='flex flex-col items-center mb-8'>
            <Link href='/' className='relative h-[60px] w-[120px] block mb-4'>
              <Image
                src={getLogoSrc()}
                alt='X-Space Real Estate'
                fill
                className='object-contain'
                sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
              />
            </Link>
            <p className='text-gray-600 dark:text-gray-400 text-center max-w-md mb-6'>
              {t('footerDescription')}
            </p>
            <div className='flex space-x-6 mb-8'>
              <a
                href='http://wa.me/9718009889'
                aria-label='Chat on WhatsApp'
                className='text-primarycolor hover:text-secondarycolor transition-colors'
              >
                <SiWhatsapp className='h-6 w-6' aria-hidden='true' />
              </a>
              <a
                href='https://www.facebook.com/xspacerealestate'
                aria-label='Visit our Facebook page'
                className='text-primarycolor hover:text-secondarycolor transition-colors'
              >
                <RiFacebookFill className='h-6 w-6' aria-hidden='true' />
              </a>
              <a
                href='https://www.instagram.com/xspace_realestate'
                aria-label='Visit our Instagram page'
                className='text-primarycolor hover:text-secondarycolor transition-colors'
              >
                <FaInstagram className='h-6 w-6' aria-hidden='true' />
              </a>
              <a
                href='https://www.linkedin.com/feed/update/urn:li:activity:7176518870264938498'
                aria-label='Visit our Linkedin page'
                className='text-primarycolor hover:text-secondarycolor transition-colors'
              >
                <BiLogoLinkedin className='h-6 w-6' aria-hidden='true' />
              </a>
              <a
                href='https://www.tiktok.com/@xspace_realestate?_t=ZS-8uVY38cKpRy&_r=1'
                aria-label='Visit our TikTok page'
                className='text-primarycolor hover:text-secondarycolor transition-colors'
              >
                <BiLogoTiktok className='h-6 w-6' aria-hidden='true' />
              </a>
            </div>
          </div>

          <div className='grid grid-cols-2 gap-8 mb-8'>
            <div>
              <h2 className='text-sm font-semibold text-gray-900 uppercase dark:text-white mb-4'>
                {t('company')}
              </h2>
              <nav className='text-gray-500 dark:text-gray-400 font-medium'>
                <ul className='space-y-3'>
                  <li>
                    <Link
                      href='/about'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('aboutUs')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href='/careers'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('careers')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href='/blog'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('blog')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href='/services'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('services')}
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>
            <div>
              <h2 className='text-sm font-semibold text-gray-900 uppercase dark:text-white mb-4'>
                {t('properties')}
              </h2>
              <nav className='text-gray-500 dark:text-gray-400 font-medium'>
                <ul className='space-y-3'>
                  <li>
                    <Link
                      href='/properties/buy'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('buy')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href='/properties/rent'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('rent')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href='/properties/off-plan'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('offPlan')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href='/properties/commercial'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('commercial')}
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>
          </div>

          <div className='mb-8'>
            <h2 className='text-sm font-semibold text-gray-900 uppercase dark:text-white mb-4'>
              {t('contactInfo')}
            </h2>
            <div className='text-gray-500 dark:text-gray-400 font-medium'>
              <ul className='space-y-3'>
                <li className='flex items-center space-x-3'>
                  <Clock className='h-5 w-5 text-primarycolor flex-shrink-0' />
                  <span>{t('workingHours')}</span>
                </li>
                <li className='flex items-center space-x-3'>
                  <Phone className='h-5 w-5 text-primarycolor flex-shrink-0' />
                  <a
                    href='tel:04-3965199'
                    className='hover:text-primarycolor dark:hover:text-secondarycolor'
                  >
                    04-3965199
                  </a>
                </li>
                <li className='flex items-center space-x-3'>
                  <Mail className='h-5 w-5 text-primarycolor flex-shrink-0' />
                  <a
                    href='mailto:info@x-space.ae'
                    className='hover:text-primarycolor dark:hover:text-secondarycolor'
                  >
                    info@x-space.ae
                  </a>
                </li>
                <li className='flex items-center space-x-3'>
                  <MapPin className='h-5 w-5 text-primarycolor flex-shrink-0' />
                  <span>{t('location')}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Desktop Footer */}
        <div className='hidden lg:block'>
          <div className='grid grid-cols-12 gap-8'>
            {/* Logo and Description */}
            <div className='col-span-4'>
              <Link href='/' className='relative h-[60px] w-[120px] block mb-4'>
                <Image
                  // src='https://images.x-space.ae/assets/images/Logo-B.png'
                  src={getLogoSrc()}
                  alt='X-Space Real Estate'
                  fill
                  className='object-contain'
                  sizes='(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw'
                />
              </Link>
              <p className='text-gray-600 dark:text-gray-400 max-w-md mb-6'>
                {t('footerDescription')}
              </p>
              <div className='flex space-x-6'>
                <a
                  href='http://wa.me/9718009889'
                  aria-label='Chat on WhatsApp'
                  className='text-primarycolor hover:text-secondarycolor transition-colors'
                >
                  <SiWhatsapp className='h-6 w-6' aria-hidden='true' />
                </a>
                <a
                  href='https://www.facebook.com/xspacerealestate'
                  aria-label='Visit our Facebook page'
                  className='text-primarycolor hover:text-secondarycolor transition-colors'
                >
                  <RiFacebookFill className='h-6 w-6' aria-hidden='true' />
                </a>
                <a
                  href='https://www.instagram.com/xspace_realestate'
                  aria-label='Visit our Instagram page'
                  className='text-primarycolor hover:text-secondarycolor transition-colors'
                >
                  <FaInstagram className='h-6 w-6' aria-hidden='true' />
                </a>
                <a
                  href='https://www.linkedin.com/feed/update/urn:li:activity:7176518870264938498'
                  aria-label='Visit our Linkedin page'
                  className='text-primarycolor hover:text-secondarycolor transition-colors'
                >
                  <BiLogoLinkedin className='h-6 w-6' aria-hidden='true' />
                </a>
                <a
                  href='https://www.tiktok.com/@xspace_realestate?_t=ZS-8uVY38cKpRy&_r=1'
                  aria-label='Visit our TikTok page'
                  className='text-primarycolor hover:text-secondarycolor transition-colors'
                >
                  <BiLogoTiktok className='h-6 w-6' aria-hidden='true' />
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div className='col-span-2'>
              <h2 className='mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white'>
                {t('company')}
              </h2>
              <nav className='text-gray-500 dark:text-gray-400 font-medium'>
                <ul className='space-y-3'>
                  <li>
                    <Link
                      href='/about'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('aboutUs')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href='/careers'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('careers')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href='/blog'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('blog')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href='/services'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('services')}
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>

            <div className='col-span-2'>
              <h2 className='mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white'>
                {t('properties')}
              </h2>
              <nav className='text-gray-500 dark:text-gray-400 font-medium'>
                <ul className='space-y-3'>
                  <li>
                    <Link
                      href='/properties/buy'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('buy')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href='/properties/rent'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('rent')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href='/properties/off-plan'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('offPlan')}
                    </Link>
                  </li>
                  <li>
                    <Link
                      href='/properties/commercial'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      {t('commercial')}
                    </Link>
                  </li>
                </ul>
              </nav>
            </div>

            <div className='col-span-4'>
              <h2 className='mb-6 text-sm font-semibold text-gray-900 uppercase dark:text-white'>
                {t('contactInfo')}
              </h2>
              <div className='text-gray-500 dark:text-gray-400 font-medium'>
                <ul className='space-y-3'>
                  <li className='flex items-center space-x-3'>
                    <Clock className='h-5 w-5 text-primarycolor flex-shrink-0' />
                    <span>{t('workingHours')}</span>
                  </li>
                  <li className='flex items-center space-x-3'>
                    <Phone className='h-5 w-5 text-primarycolor flex-shrink-0' />
                    <a
                      href='tel:04-3965199'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      04-3965199
                    </a>
                  </li>
                  <li className='flex items-center space-x-3'>
                    <Mail className='h-5 w-5 text-primarycolor flex-shrink-0' />
                    <a
                      href='mailto:info@x-space.ae'
                      className='hover:text-primarycolor dark:hover:text-secondarycolor'
                    >
                      info@x-space.ae
                    </a>
                  </li>
                  <li className='flex items-center space-x-3'>
                    <MapPin className='h-5 w-5 text-primarycolor flex-shrink-0' />
                    <span>{t('location')}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className='mt-12 border-t border-gray-900/10 dark:border-white/10 pt-8'>
          <div className='flex flex-col items-center justify-between gap-4 sm:flex-row'>
            <p className='text-sm text-gray-600 dark:text-gray-400 text-center sm:text-left'>
              © {new Date().getFullYear()} X-SPACE Real Estate™. All rights
              reserved.
            </p>
            <div className='flex flex-wrap justify-center gap-4 text-sm'>
              <Link
                href='/privacy-policy'
                className='text-gray-600 hover:text-primarycolor dark:text-gray-400 dark:hover:text-secondarycolor'
              >
                {t('privacyPolicy')}
              </Link>
              <Link
                href='/terms-of-service'
                className='text-gray-600 hover:text-primarycolor dark:text-gray-400 dark:hover:text-secondarycolor'
              >
                {t('termsOfService')}
              </Link>
              <Link
                href='/cookies-policy'
                className='text-gray-600 hover:text-primarycolor dark:text-gray-400 dark:hover:text-secondarycolor'
              >
                {t('cookiesPolicy')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
